//     ininAudioPlayer
// Provides a common interface to HTML5 Audio/Flash audio - one of which is chosen based on support (HTML5 preferred)
// There are external dependencies on jQuery and swfObject and some markup that needs to be added to host page (perhaps this
// can be made dynamic as the flash markup causes FF to prompt to install flash (even though it actually will use HTML5)

import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import LOG from "services/loggingService";
import ininAudioUtils from "util/ininAudioUtils";

export default function InInAudioPlayer() {
    const self = this;
    self.events = {};
    self.pausedPosition = 0;

    self.canPlayWithFlash = false;
    self.canPlayWithHTML5 = false;
    self.useHTML5 = false;

    self.ready = false;

    self.mimeType = "";
    self.acceptedMimeTypes = [];

    self.eventTypes = {
        CANPLAY: "canplay",
        CANPLAYTHROUGH: "canplaythrough",
        ENDED: "ended",
        LOADED: "loaded",
        LOADEDDATA: "loadeddata",
        READY: "ready",
        SEEKING: "seeking",
        SEEKED: "seeked",
        STALLED: "stalled",
        TIMEUPDATE: "timeupdate",
        LOG: "log",
    };

    self.addListener = function (eventName, callback) {
        const callbacks = (self.events[eventName] =
            self.events[eventName] || []);
        callbacks.push(callback);
    };

    self.init = function (html5AudioElement, mimeType) {
        //ininAudioUtils will suggest a mimeType/formatId
        self.mimeType = ininAudioUtils.getSuggestedMimeType();
        const supportsNativeAudio = ininAudioUtils.getIsNativeFormatAvailable();

        //If consumer is choosing to override our suggested type, use that
        if (mimeType) {
            self.mimeType = mimeType;
        } else {
            self.acceptedMimeTypes = ininAudioUtils.getAllAcceptedMimeTypes();
        }

        if (!self.acceptedMimeTypes.length) {
            LOG.info("Preparing for " + self.mimeType + " playback");
        } else {
            LOG.info(
                "Preparing for one of " + self.acceptedMimeTypes + " playback"
            );
        }

        self.raiseEvent = function (eventName, args) {
            const callbacks = self.events[eventName];
            if (callbacks) {
                for (let i = 0, l = callbacks.length; i < l; i++) {
                    callbacks[i].call(null, args);
                }
            }
        };

        if (html5AudioElement && supportsNativeAudio) {
            self.canPlayWithHTML5 = false;
            if (html5AudioElement.canPlayType) {
                // we don't know specifically what format/mimeType the return value will be
                // Check that we can play all potential returned mime types with HTML5
                self.canPlayWithHTML5 = self.acceptedMimeTypes.every(
                    (mimeType) =>
                        html5AudioElement.canPlayType(mimeType).length > 0
                );
                if (self.canPlayWithHTML5) {
                    self.useHTML5 = true;
                    initForHTML5(html5AudioElement);
                    self.ready = true;
                    self.raiseEvent(self.eventTypes.READY);
                }
            }
        }

        if (!self.useHTML5) {
            LOG.warn("No audio will be available");
        }
    };

    //Abstraction object, will be mapped for HTML5 or Flash use
    const audioObj = {};

    const initForHTML5 = function (html5AudioElement) {
        audioObj.load = function (url) {
            html5AudioElement.src = url;
            html5AudioElement.load();
        };

        audioObj.play = function (position) {
            if (position) {
                html5AudioElement.currentTime = position / 1000.0;
            }
            html5AudioElement.play();
        };

        audioObj.pause = function () {
            html5AudioElement.pause();
        };

        audioObj.setSpeed = function (speed) {
            html5AudioElement.playbackRate = speed;
        };

        audioObj.getPosition = function () {
            return html5AudioElement.currentTime * 1000.0;
        };

        audioObj.setPosition = function (position) {
            if (!isNaN(position)) {
                html5AudioElement.currentTime = position / 1000.0;
            }
        };

        audioObj.getDuration = function () {
            return html5AudioElement.duration * 1000.0;
        };

        audioObj.getIsPlaying = function () {
            return !html5AudioElement.paused;
        };

        audioObj.setVolumeLevel = function (level) {
            let volume = level / 100;
            volume = volume < 0 ? 0 : volume;
            volume = volume > 1 ? 1 : volume;
            html5AudioElement.volume = volume;
        };

        audioObj.getVolumeLevel = function () {
            const volume = html5AudioElement.volume * 100;
            return volume;
        };

        audioObj.reset = function () {
            html5AudioElement.pause();
        };

        audioObj.getReadyState = function () {
            return html5AudioElement.readyState;
        };

        html5AudioElement.addEventListener(
            self.eventTypes.CANPLAY,
            function () {
                self.raiseEvent(self.eventTypes.CANPLAY);
            }
        );

        html5AudioElement.addEventListener(
            self.eventTypes.CANPLAYTHROUGH,
            function () {
                self.raiseEvent(self.eventTypes.CANPLAYTHROUGH);
            }
        );

        html5AudioElement.addEventListener(
            self.eventTypes.SEEKING,
            function () {
                self.raiseEvent(self.eventTypes.SEEKING);
            }
        );

        html5AudioElement.addEventListener(self.eventTypes.SEEKED, function () {
            self.raiseEvent(
                self.eventTypes.SEEKED,
                parseFloat(html5AudioElement.currentTime * 1000.0)
            );
        });

        html5AudioElement.addEventListener(
            self.eventTypes.STALLED,
            function () {
                self.raiseEvent(self.eventTypes.STALLED);
            }
        );

        html5AudioElement.addEventListener(self.eventTypes.ENDED, function () {
            self.raiseEvent(self.eventTypes.ENDED);
        });

        html5AudioElement.addEventListener(self.eventTypes.LOADED, function () {
            self.raiseEvent(self.eventTypes.LOADED);
        });

        html5AudioElement.addEventListener(
            self.eventTypes.LOADEDDATA,
            function () {
                self.raiseEvent(self.eventTypes.LOADEDDATA);
            }
        );

        html5AudioElement.addEventListener(
            self.eventTypes.TIMEUPDATE,
            function () {
                self.raiseEvent(
                    self.eventTypes.TIMEUPDATE,
                    parseFloat(html5AudioElement.currentTime * 1000.0)
                );
            }
        );
    };

    //Public interface for various functions/info
    self.load = function (url) {
        if (audioObj.load) {
            audioObj.load(url);
        }
    };

    self.play = function (position) {
        if (audioObj.play) {
            return audioObj.play(position);
        }
    };

    self.setSpeed = function (speed) {
        if (audioObj.setSpeed) {
            return audioObj.setSpeed(speed);
        }
    };

    self.pause = function () {
        if (audioObj.pause) {
            return audioObj.pause();
        }
    };

    self.stop = function () {
        if (audioObj.pause) {
            if (audioObj.setPosition) {
                audioObj.setPosition(0);
            }
            return audioObj.pause();
        }
    };

    self.getPosition = function () {
        if (audioObj.getPosition) {
            const position = audioObj.getPosition();
            if (position && !isNaN(position)) {
                return position;
            }
        }
        return 0;
    };

    self.setPosition = function (position) {
        if (audioObj.setPosition) {
            audioObj.setPosition(position);
        }
    };

    self.getDuration = function () {
        if (audioObj.getDuration) {
            const duration = audioObj.getDuration();
            if (duration && !isNaN(duration)) {
                return duration;
            }
        }
        return 0;
    };

    self.getIsPlaying = function () {
        if (audioObj.getIsPlaying) {
            return audioObj.getIsPlaying();
        }
        return false;
    };

    self.setVolumeLevel = function (level) {
        if (audioObj.setVolumeLevel) {
            audioObj.setVolumeLevel(level);
        }
    };

    self.getVolumeLevel = function () {
        if (audioObj.getVolumeLevel) {
            return audioObj.getVolumeLevel();
        }
    };

    self.reset = function () {
        if (audioObj.reset) {
            return audioObj.reset();
        }
    };

    self.getReadyState = function () {
        if (audioObj.getReadyState) {
            return audioObj.getReadyState();
        }
    };
}
